import { useCart } from "@/contexts/CartContext";
import StoreLayout from "@/layouts/storeLayout";
import { Button } from "@/app/global-components/button";
import { Card, CardContent } from "@/app/global-components/card";
import {
  Trash2,
  Plus,
  Minus,
  ShoppingCart,
  Package,
  DollarSign,
  ArrowLeft,
  CreditCard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { routes } from "@/consts";
import { motion } from "framer-motion";

export default function Cart() {
  const { cartItems, removeFromCart, clearCart, updateQuantity, totalPrice } =
    useCart();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  if (cartItems.length === 0) {
    return (
      <StoreLayout>
        <div className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md mx-auto"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingCart className="w-10 h-10 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold mb-4 text-gray-900">
              {t("buyer_pages.cart.empty_cart")}
            </h1>
            <p className="text-gray-600 mb-6">
              {t("buyer_pages.cart.empty_cart_description") ||
                "Your cart is empty. Start shopping to add items."}
            </p>
            <Button
              onClick={() => navigate(routes.marketPlace)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t("buyer_pages.cart.continue")}
            </Button>
          </motion.div>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t("buyer_pages.cart.product")}
              </h1>
              <p className="text-gray-600 mt-1">
                {cartItems.length} {cartItems.length === 1 ? "item" : "items"}{" "}
                {t("checkout.inCart")}
              </p>
            </div>
          </div>
          <Button
            variant="destructive"
            onClick={clearCart}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-medium transition-all duration-200"
          >
            {t("buyer_pages.cart.clear_cart") || "Clear Cart"}
          </Button>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-0  transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      {/* Product Image */}
                      <div className="w-24 h-24 flex-shrink-0">
                        <img
                          src={item?.images[0]}
                          alt={item?.name}
                          className="w-full h-full object-cover rounded-xl shadow-md"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900 mb-1">
                              {item?.name}
                            </h3>
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-4 h-4 text-gray-500" />
                              <p className="text-sm text-gray-600">
                                {item?.store?.name}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-emerald-600" />
                              <p className="text-emerald-600 font-bold text-lg">
                                {item?.price.toFixed(2)} {t("currency")}/
                                {item?.unit}
                              </p>
                            </div>
                          </div>

                          {/* Remove Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-3 mt-4">
                          <span className="text-sm text-gray-600 font-medium">
                            Quantity:
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                              className="w-8 h-8 rounded-lg border-gray-300 hover:bg-gray-50"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-12 text-center font-semibold text-gray-900">
                              {item?.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() =>
                                updateQuantity(item?.id, item?.quantity + 1)
                              }
                              className="w-8 h-8 rounded-lg border-gray-300 hover:bg-gray-50"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="border-0 shadow-none sticky top-4">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {t("buyer_pages.cart.order_summary") || "Order Summary"}
                    </h2>
                  </div>

                  <div className="space-y-4">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-600">
                        {t("buyer_pages.cart.subtotal")}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {totalPrice?.toFixed(2)} {t("currency")}
                      </span>
                    </div>

                    {/* Shipping */}
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-600">
                        {t("buyer_pages.cart.shipping") || "Shipping"}
                      </span>
                      <span className="font-semibold text-green-600">
                        {t("buyer_pages.cart.free") || "Free"}
                      </span>
                    </div>

                    {/* Tax */}
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-600">
                        {t("buyer_pages.cart.tax") || "Tax"}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {(totalPrice * 0.15).toFixed(2)} {t("currency")}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-4"></div>

                    {/* Total */}
                    <div className="flex justify-between items-center py-3">
                      <span className="text-lg font-bold text-gray-900">
                        {t("buyer_pages.cart.total")}
                      </span>
                      <span className="text-xl font-bold text-zinc-600">
                        {(totalPrice * 1.15).toFixed(2)} {t("currency")}
                      </span>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-zinc-900  text-white py-3 rounded-xl font-medium transition-all duration-200 mt-6"
                    onClick={() => navigate("/checkout")}
                  >
                    {t("buyer_pages.cart.proceed")}
                  </Button>

                  
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}
